// Shared base for the three transactional CMS "picker" widgets (review-picker,
// praise-picker, theme-quote-picker). All three share an identical transactional
// shell — a working-copy `selected` list edited inside a modal, a pre-edit snapshot
// for clean discard, a confirmed rail snapshot, and ONE whole-value
// cms-image-changed event on confirm — differing only in their item type and their
// available()/add()/remove() pool logic (+ a couple of per-picker extras).
//
// createTransactionalPicker() owns the shell; each concrete picker spreads it and
// supplies its pool + available()/add()/remove() (and extras like review's
// MIN/byId/selectedReviews or theme-quote's themeLabels/displayTheme).
//
// IMPORTANT (Alpine): every method here is a normal (non-arrow) shorthand method so
// `this` resolves to the live Alpine component proxy the concrete object is wrapped
// in — never capture `this` in an arrow at this layer.

import Sortable from "sortablejs";
import { applySortableReorder, createReorderable } from "../shared/sortable-reorder";

export interface TransactionalPickerConfig<S> {
  field: string;
  // Autosave routing key — threaded into the cms-image-changed detail so the
  // cmsEditor controller routes the write to the right section overlay.
  target: string;
  // Already-parsed working selection (the concrete picker parses the raw config).
  selected: S[];
  // Selection cap (atMax()).
  max: number;
}

// The transactional shell, generic over the SELECTED item type `S` (a string id for
// review/praise, a {theme,quote} object for theme-quote). `pool` is owned by the
// concrete picker because its type can differ from `S` (review's pool is Review[]
// while its selection is string[]).
export function createTransactionalPicker<S>(cfg: TransactionalPickerConfig<S>) {
  return {
    field: cfg.field,
    target: cfg.target,
    // Working copy edited inside the modal.
    selected: [...cfg.selected] as S[],
    search: "",
    MAX: cfg.max,
    // Rail-summary snapshot (only updated on confirm()), seeded from the load.
    _confirmed: [...cfg.selected] as S[],
    // Pre-edit deep copy captured on openModal(), restored on discard().
    _preEdit: null as S[] | null,
    // Live Sortable instances so destroy() can tear them down.
    _sortables: [] as Sortable[],

    // SortableJS lives on the stable <ul x-ref="selList">; the selected list's
    // keyed x-for only swaps the <li> children, so the instance survives
    // add/remove. If the list isn't rendered (empty modal), the helper no-ops.
    init() {
      (this as any).$nextTick(() => this._initSortable());
    },

    destroy() {
      for (const s of this._sortables) s.destroy();
      this._sortables = [];
    },

    // Working-copy count + the upper constraint helper for the modal display.
    count(): number {
      return this.selected.length;
    },
    atMax(): boolean {
      return this.selected.length >= this.MAX;
    },

    // Rail summary: the CONFIRMED count (last-saved, not in-flight modal edits).
    summary(): number {
      return this._confirmed.length;
    },

    // Snapshot the working copy into a pre-edit deep copy so discard() can restore
    // cleanly. JSON round-trip (items are plain strings or {theme,quote} objects).
    openModal() {
      this._preEdit = JSON.parse(JSON.stringify(this.selected)) as S[];
    },

    // Commit the working copy: refresh the rail snapshot, then fire ONE whole-value
    // cms-image-changed event on $root so the cmsEditor controller autosaves it.
    // The templ then closes the dialog.
    confirm() {
      this._confirmed = [...this.selected];
      this._preEdit = null;
      const root = (this as any).$root as HTMLElement;
      root.dispatchEvent(
        new CustomEvent("cms-image-changed", {
          detail: { field: this.field, target: this.target, value: [...this.selected] },
          bubbles: true,
        }),
      );
    },

    // Restore the pre-edit copy (no event fired).
    discard() {
      this.selected = this._preEdit ? [...this._preEdit] : this.selected;
      this._preEdit = null;
    },

    // SortableJS on the SELECTED list only: reorders `selected` via the
    // [data-sort-handle] grip. The pool is click-add (not reorderable) and there is
    // no shared group, so no cross-list drag / applySortableMove. No emit on reorder
    // — confirm() fires the single whole-value event. applySortableReorder undoes
    // Sortable's DOM move THEN reorders the array so Alpine's keyed x-for doesn't
    // move the node a second time (the snap-back/desync bug).
    _initSortable() {
      const el = (this as any).$refs.selList as HTMLElement | undefined;
      if (!el) return;
      this._sortables.push(
        createReorderable(el, {
          handle: "[data-sort-handle]",
          draggable: "[data-sort-id]",
          onEnd: (e: Sortable.SortableEvent) => {
            this.selected = applySortableReorder(
              el,
              e,
              this.selected as unknown[],
              "data-sort-id",
            ) as S[];
          },
        }),
      );
    },
  };
}

// The slice of the live Alpine picker proxy that the pool-picker trio reads. A
// concrete picker exposes `pool` (its own item type `P`), the working `selected`
// list of string keys, the `search` box, and the confirmed `MAX` cap — all
// supplied by the concrete picker + createTransactionalPicker().
interface PoolPickerCtx<P> {
  pool: P[];
  selected: string[];
  search: string;
  MAX: number;
}

// Per-picker pool accessors: how to derive an item's selection key and how to
// match it against the (already trimmed + lowercased) search query.
export interface PoolPickerConfig<P> {
  // The string key stored in `selected` for a pool item (e.g. `p => p.id`, or
  // `p => p` when the pool items ARE the stored strings).
  keyOf: (item: P) => string;
  // Case-insensitive search predicate. `q` is pre-trimmed + lowercased and never
  // empty (the empty-query short-circuit lives in available()).
  matches: (item: P, q: string) => boolean;
}

// The click-to-add / click-to-remove POOL logic shared byte-for-byte by the
// string-keyed pickers (review / area-POI / praise). They differ ONLY in the
// pool item type and which field(s) the search box filters — both captured by
// `cfg` — so available()/add()/remove() lift out verbatim here. theme-quote is
// NOT one of these: its selection is {theme,quote} objects with one-quote-per-
// theme semantics, so it keeps its own trio.
//
// Spread into the concrete picker alongside createTransactionalPicker(); every
// method is a normal shorthand so `this` binds to the live Alpine proxy.
export function poolPickerMethods<P>(cfg: PoolPickerConfig<P>) {
  return {
    // Pool items not yet selected, filtered by the search box — the POOL panel
    // (click-to-add).
    available(this: PoolPickerCtx<P>): P[] {
      const q = this.search.trim().toLowerCase();
      return this.pool.filter((p) => {
        if (this.selected.includes(cfg.keyOf(p))) return false;
        if (!q) return true;
        return cfg.matches(p, q);
      });
    },

    // Add a key to the selection (no-op if already selected or at MAX).
    add(this: PoolPickerCtx<P>, key: string) {
      if (this.selected.includes(key)) return;
      if (this.selected.length >= this.MAX) return;
      this.selected = [...this.selected, key];
    },

    // Drop a key from the selection.
    remove(this: PoolPickerCtx<P>, key: string) {
      const i = this.selected.indexOf(key);
      if (i === -1) return;
      this.selected.splice(i, 1);
    },
  };
}
